import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import { RelatedGuides } from "@/components/related-guides";
import { SiteFooter } from "@/components/site-footer";

export const FAQS = [
  {
    q: "What is vibe coding?",
    a: "Vibe coding is a way of building software where you describe what you want in plain language and an AI tool — Lovable, Cursor, Claude Code, Codex — writes the code. You stay in the role of director: you choose the vibe, the AI handles the syntax.",
  },
  {
    q: "Who coined vibe coding?",
    a: "The term was popularised by Andrej Karpathy in early 2025. He described handing off code generation to an LLM and 'forgetting the code even exists'. The phrase stuck because it named something thousands of people were already doing.",
  },
  {
    q: "Is vibe coding the same as no-code?",
    a: "No. No-code tools hide code behind a visual editor with a fixed feature set. Vibe coding produces real code — React, TypeScript, SQL — that you can read, fork, and host anywhere. The ceiling is much higher and so is the floor.",
  },
  {
    q: "Is vibe coding bad?",
    a: "Vibe coding is great for shipping fast. It's bad when you ship without anyone checking the security, the data model, or the bundle size. The honest answer: vibe coding plus an engineer review is a superpower; vibe coding alone in production is a liability.",
  },
  {
    q: "What tools do people vibe code with?",
    a: "The four most common in 2026: Lovable (for full apps with a backend), Cursor (for pair-programming inside your IDE), Claude Code (for agentic terminal work), and Codex (OpenAI's coding agent). Most serious vibe coders use two or three together.",
  },
  {
    q: "Can I hire someone to vibe code with me?",
    a: "Yes. We're a vibe coding agency — we sit in your Lovable workspace, ship the parts you can't, and leave you with a project you can keep prompting on.",
  },
];

export const Route = createFileRoute("/guides/what-is-vibe-coding")({
  head: () => ({
    meta: [
      { title: "What Is Vibe Coding? A 2026 Guide | the Vibing Company®" },
      {
        name: "description",
        content:
          "Vibe coding explained: what it is, who coined it, the tools (Lovable, Cursor, Claude Code, Codex), where it works, where it breaks, and how to do it safely.",
      },
      { property: "og:title", content: "What Is Vibe Coding? A 2026 Guide" },
      ...ogImageMeta("guides-what-is-vibe-coding", "Share card: what is vibe coding"),
      {
        property: "og:description",
        content:
          "Vibe coding explained — the term, the tools, the wins, the failure modes, and how to ship a vibe-coded app you'd actually trust in production.",
      },
      { property: "og:url", content: "https://vibing.inc/guides/what-is-vibe-coding" },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/guides/what-is-vibe-coding" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "What Is Vibe Coding? A 2026 Guide",
          description:
            "Vibe coding explained: definition, origin, tools, strengths, failure modes, and how to ship safely.",
          author: { "@type": "Organization", name: "the Vibing Company" },
          publisher: { "@type": "Organization", name: "the Vibing Company" },
          datePublished: "2026-06-11",
          mainEntityOfPage: "https://vibing.inc/guides/what-is-vibe-coding",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Guide,
});

function Guide() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Body />
      <FAQ />
      <CTA />
      <RelatedGuides current="/guides/what-is-vibe-coding" service={{ to: "/services/lovable-development", label: "Lovable development" }} />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="g-h" className="relative mx-auto max-w-[1100px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-12">
      <h1 id="g-h" className="mt-8 font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
        What is vibe coding?
      </h1>
      <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
        A working definition, who coined it, the four tools that matter, where vibe coding wins, where it breaks, and how to ship a vibe-coded app you'd actually put in front of a customer.
      </p>
    </section>
  );
}

const SECTIONS = [
  {
    h: "A working definition",
    p: [
      "Vibe coding is building software by describing what you want in plain language and letting an AI tool generate the code. The human picks the destination and the feel — the vibe — and the AI handles the syntax, the boilerplate, and most of the typing.",
      "You're not reading every line. You're reading diffs, testing screens, and prompting again when something is off. The unit of work shifts from 'function' to 'intent'.",
    ],
  },
  {
    h: "Where the term came from",
    p: [
      "Andrej Karpathy posted in February 2025 that he was 'fully giving in to the vibes' and forgetting the code even existed. Within a week the phrase was everywhere — partly because it was funny, mostly because it named a thing thousands of people were quietly already doing.",
      "Before 'vibe coding' the same activity went under names like 'prompt-driven development' or just 'using Copilot'. The new name made it a category. Categories get tools, agencies, conferences, and arguments.",
    ],
  },
  {
    h: "The four tools that matter in 2026",
    p: [
      "Lovable is the full-stack option — describe an app, get a working app with a database, auth, and a public URL. Cursor is the IDE most serious vibe coders open every morning; it pairs with you on a real codebase. Claude Code lives in the terminal and runs as an agent — give it a task and walk away. Codex is OpenAI's answer to Cursor and Claude Code, fast and increasingly capable.",
      "Most people who vibe code professionally use at least two: Lovable for the first 80% of a new app, Cursor or Claude Code for the long tail of fixes and refactors.",
    ],
  },
  {
    h: "Where vibe coding wins",
    p: [
      "Speed from zero. An MVP that used to take a team three months now takes one person five days. That changes which ideas are worth trying — the cost of being wrong collapses.",
      "Internal tools. Throwaway dashboards, scripts, one-off forms. Vibe coding is perfect for software that only needs to work once or twice.",
      "Founders prototyping in customer conversations. You can change a screen while the customer is still on the call.",
    ],
  },
  {
    h: "Where vibe coding breaks",
    p: [
      "Security. AI tools default to insecure patterns — exposed keys, missing row-level security, no input validation. A vibe-coded app put into production without a review is a data leak waiting to happen.",
      "Codebase rot. After a few weeks of prompts the project has dead files, three competing patterns, and the AI starts contradicting itself. The vibe-coded speed only lasts as long as the codebase stays readable.",
      "Hard engineering problems. Anything involving payments reconciliation, real concurrency, complex permissions, or third-party integrations needs a human who understands what the AI is doing.",
    ],
  },
  {
    h: "How to vibe code safely",
    p: [
      "Pair the vibe with a review. Either learn enough to read your own diffs, or hire someone who does. Even one hour a week of senior eyes will catch the worst category of mistakes.",
      "Keep the project boring. Resist the urge to add a third framework, a fourth state library, or a custom build step. Boring vibe-coded apps stay vibe-codeable.",
      "Audit before launch. Run a security pass before you put a payment form online. Most launch-day disasters were preventable in an afternoon.",
    ],
  },
];

function Body() {
  return (
    <article className="mx-auto max-w-[1100px] px-6 md:px-10 py-12 md:py-20 space-y-16 md:space-y-24">
      {SECTIONS.map((s, i) => (
        <section key={s.h} className="grid md:grid-cols-12 gap-8 md:gap-10 border-t border-border/60 pt-12 md:pt-16">
          <div className="md:col-span-4">
            <h2 className="mt-4 font-display tracking-[-0.03em] leading-[1.02]" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
              {s.h}
            </h2>
          </div>
          <div className="md:col-span-8 space-y-5 text-base md:text-lg leading-relaxed text-foreground/90 max-w-[64ch]">
            {s.p.map((para, j) => (
              <p key={j}>{para}</p>
            ))}
          </div>
        </section>
      ))}
    </article>
  );
}

function FAQ() {
  return (
    <section aria-labelledby="faq-h" className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="faq-h" className="fluid-h2 font-display max-w-[18ch]">Vibe coding, answered.</h2>
      <dl className="mt-14 border-t border-border">
        {FAQS.map((f) => (
          <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
            <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">{f.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 id="cta-h" className="font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
            Want to vibe code with a pro?
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
            We're a vibe coding agency. We sit in your Lovable workspace and shape the right engagement around the parts you cannot ship alone.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-9 space-y-4">
          <Link to="/services/lovable-development" className="block font-display text-electric-bright" style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)" }}>
            → Hire a Lovable expert
          </Link>
          <Link to="/services/vibe-coding-rescue" className="block font-display text-electric-bright" style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)" }}>
            → Rescue a stuck project
          </Link>
        </div>
      </div>
    </section>
  );
}
